import type {
  TerminalRecordingEvent,
  TerminalRecordingSession,
} from "@p0security/shared/integrations/sessions/types";

export const findCutoffIndex = (
  events: readonly TerminalRecordingEvent[],
  playheadSeconds: number
): number => {
  let lo = 0;
  let hi = events.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (events[mid]!.atSeconds <= playheadSeconds) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
};

// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE_RE =
  /\u001b(?:\[[0-9;?]*[A-Za-z]|\][^\u0007]*\u0007|\([AB012])/g;

const measureVisibleLineWidths = (
  events: readonly TerminalRecordingEvent[]
): number => {
  let maxWidth = 0;
  for (const event of events) {
    if (event.type !== "o" || !event.data) continue;
    const stripped = event.data.replace(ANSI_ESCAPE_RE, "");
    for (const line of stripped.split("\n")) {
      // CR resets cursor to column 0; only the last segment is visible.
      // Filter empty trailing segments so "\r\n" line endings don't zero out.
      const segments = line.split("\r").filter(Boolean);
      const visible = segments[segments.length - 1] ?? "";
      maxWidth = Math.max(maxWidth, visible.length);
    }
  }
  return maxWidth;
};

export const inferTerminalDimensions = (
  session: Pick<TerminalRecordingSession, "cols" | "events" | "rows">
): Pick<TerminalRecordingSession, "cols" | "rows"> => {
  if (session.cols && session.cols > 1 && session.rows) {
    return {
      cols: session.cols,
      rows: session.rows,
    };
  }

  let inferredCols = session.cols ?? 0;
  let inferredRows = session.rows ?? 0;

  for (const event of session.events) {
    if (event.type !== "o" || !event.data) {
      continue;
    }

    for (const match of event.data.matchAll(/\u001b\[(\d+);(\d+)[Hf]/g)) {
      inferredRows = Math.max(
        inferredRows,
        Number.parseInt(match[1] ?? "", 10)
      );
      inferredCols = Math.max(
        inferredCols,
        Number.parseInt(match[2] ?? "", 10)
      );
    }

    for (const match of event.data.matchAll(/\u001b\[(\d+);(\d+)r/g)) {
      inferredRows = Math.max(
        inferredRows,
        Number.parseInt(match[2] ?? "", 10)
      );
    }
  }

  // Cursor position escapes only tell us the max column *visited*, not the
  // terminal width. Programs like `top` position at column 1 then write
  // full-width lines, so escape-based cols can be far too small.
  if (inferredCols === 1) {
    const lineWidth = measureVisibleLineWidths(session.events);
    if (lineWidth > inferredCols) {
      inferredCols = lineWidth;
    }
  }

  return {
    cols: inferredCols > 0 ? inferredCols : undefined,
    rows: inferredRows > 0 ? inferredRows : undefined,
  };
};
